// DocStore: a Y.Doc per subscribed doc, wired to PersistencePort (updates
// appended on change, local compaction at 200) and the Provider (the
// y-protocols sync exchange). Plugin spec §Core engine + protocol spec
// §y-protocols framing.
//
// Origin discipline (the crux of not echoing remote edits back):
//   ORIGIN_LOCAL  — a bridge/editor edit: persist AND send to the server.
//   ORIGIN_REMOTE — a server-applied update: persist, surface to the bridge,
//                   never send back.
//   ORIGIN_LOAD   — replay of persisted updates on open: neither re-persist
//                   nor send.

import * as Y from "yjs";
import { Mode, Reason } from "@cairn/proto";
import type { PersistencePort } from "./ports";
import { Emitter } from "./emitter";
import { Awareness, encodeUpdate, readMessage } from "./yprotocol";

/** Transaction origin marking a local edit (bridge/editor) to be sent up. */
export const ORIGIN_LOCAL = Symbol("cairn:local");
const ORIGIN_REMOTE = Symbol("cairn:remote");
const ORIGIN_LOAD = Symbol("cairn:load");

/** Local compaction threshold (plugin spec §PersistencePort). */
export const COMPACT_THRESHOLD = 200;

/** The meta doc's reserved id (protocol spec §Connection lifecycle). */
export const META_DOC_ID = "meta";

/**
 * Default budget for the blocking meta-doc sync that gates `Bridge.start()` and
 * the join preview (plugin spec §Startup / §Merge-on-join). Deliberately
 * generous: this is a "give up and surface an error" ceiling, not an expected
 * latency (the meta doc normally syncs in milliseconds). 10s proved too tight
 * for a slow real network AND for a contended CI host — a value suspect on
 * localhost is suspect in production, so the ceiling is 30s and is overridable
 * via `EngineConfig.metaSyncTimeoutMs` (decisions/30-integration.md).
 */
export const META_SYNC_TIMEOUT_MS = 30_000;

/** A post-sync remote change the bridge must reconcile. */
export interface RemoteChange {
  docId: string;
  isMeta: boolean;
}

interface DocEntry {
  doc: Y.Doc;
  awareness: Awareness;
  isMeta: boolean;
  epoch: number;
  /** Initial sync exchange completed (server step 2 received). */
  synced: boolean;
  /** Set on SubscribeAck; the next applied sync frame is the initial step 2. */
  awaitingStep2: boolean;
  /** Incremental updates since the last compaction (for the 200 cap). */
  updateCount: number;
  /** Local updates captured while unsynced, flushed once synced. */
  outbox: Uint8Array[];
  observer: (update: Uint8Array, origin: unknown) => void;
}

/** What the DocStore needs from the Provider (breaks the import cycle). */
export interface DocSender {
  sendDoc(docId: string, payload: Uint8Array): boolean;
  canWrite(): boolean;
  subscribe(docId: string, mode: Mode): void;
  unsubscribe(docId: string): void;
}

export class DocStore {
  private readonly docs = new Map<string, DocEntry>();
  private sender: DocSender | null = null;

  /** Fired when a doc completes its initial sync exchange. */
  readonly synced = new Emitter<string>();
  /** Fired on a post-sync remote update (steady state). */
  readonly remoteChange = new Emitter<RemoteChange>();
  /** Fired when a doc's epoch changed and its state was dropped + resynced. */
  readonly epochChanged = new Emitter<string>();
  /** Fired when a doc frame was rejected (drives full-doc resync). */
  readonly rejected = new Emitter<{ docId: string; reason: Reason }>();

  constructor(private readonly persistence: PersistencePort) {}

  /** Wire the Provider after both exist (resolves the construction cycle). */
  attach(sender: DocSender): void {
    this.sender = sender;
  }

  /** Open (or return) a doc, replaying its persisted updates. */
  async ensureOpen(docId: string): Promise<Y.Doc> {
    const existing = this.docs.get(docId);
    if (existing) return existing.doc;

    const doc = new Y.Doc();
    const isMeta = docId === META_DOC_ID;
    const loaded = await this.persistence.loadDoc(docId);
    const epoch = loaded?.epoch ?? 0;
    if (loaded) {
      Y.transact(
        doc,
        () => {
          for (const update of loaded.updates) Y.applyUpdate(doc, update, ORIGIN_LOAD);
        },
        ORIGIN_LOAD,
      );
    }

    const entry: DocEntry = {
      doc,
      awareness: new Awareness(doc),
      isMeta,
      epoch,
      synced: false,
      awaitingStep2: false,
      updateCount: loaded?.updates.length ?? 0,
      outbox: [],
      observer: () => {},
    };
    entry.observer = (update, origin) => this.onUpdate(docId, entry, update, origin);
    doc.on("update", entry.observer);
    this.docs.set(docId, entry);
    return doc;
  }

  /** Open + subscribe a doc in the given mode. */
  async subscribe(docId: string, mode: Mode): Promise<Y.Doc> {
    const doc = await this.ensureOpen(docId);
    this.sender?.subscribe(docId, mode);
    return doc;
  }

  getDoc(docId: string): Y.Doc | undefined {
    return this.docs.get(docId)?.doc;
  }

  awareness(docId: string): Awareness | undefined {
    return this.docs.get(docId)?.awareness;
  }

  isSynced(docId: string): boolean {
    return this.docs.get(docId)?.synced ?? false;
  }

  /** Resolve once a doc has completed its initial sync exchange. */
  waitSynced(docId: string, timeoutMs = 10_000): Promise<void> {
    if (this.isSynced(docId)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const off = this.synced.on((id) => {
        if (id === docId) {
          off();
          clearTimeout(timer);
          resolve();
        }
      });
      const timer = setTimeout(() => {
        off();
        reject(new Error(`doc ${docId} did not sync within ${timeoutMs}ms`));
      }, timeoutMs);
    });
  }

  epochOf(docId: string): number {
    return this.docs.get(docId)?.epoch ?? 0;
  }

  /**
   * Override a doc's known epoch. Its next (re)subscribe carries this as
   * `known_epoch`; a value that disagrees with the server's makes the server
   * answer `epoch_changed` (protocol spec §Epochs), driving the resync +
   * rebase recovery path. Used by recovery flows and to exercise that path.
   */
  setKnownEpoch(docId: string, epoch: number): void {
    const entry = this.docs.get(docId);
    if (entry) entry.epoch = epoch;
  }

  /** Local updates buffered while offline/unsynced (status-bar pending count). */
  pendingCount(): number {
    let n = 0;
    for (const e of this.docs.values()) n += e.outbox.length;
    return n;
  }

  /** Run a local mutation inside an ORIGIN_LOCAL transaction. */
  transactLocal(docId: string, fn: (doc: Y.Doc) => void): void {
    const entry = this.docs.get(docId);
    if (!entry) return;
    Y.transact(entry.doc, () => fn(entry.doc), ORIGIN_LOCAL);
  }

  // --- ProviderCallbacks (doc-scoped) --------------------------------------

  subscribeParams(docId: string): { stateVector: Uint8Array; knownEpoch: number } {
    const entry = this.docs.get(docId);
    if (!entry) return { stateVector: new Uint8Array(), knownEpoch: 0 };
    return {
      stateVector: Y.encodeStateVector(entry.doc),
      knownEpoch: entry.epoch,
    };
  }

  async onSubscribeAck(
    docId: string,
    epoch: number,
    epochChanged: boolean,
  ): Promise<void> {
    const entry = this.docs.get(docId);
    if (!entry) return;
    entry.epoch = epoch;
    entry.awaitingStep2 = true;
    entry.synced = false;
    if (epochChanged) {
      // Drop local Yjs state; the server's step 2 rebuilds it fresh
      // (protocol spec §Recovery; plugin spec §Recovery paths). The in-memory
      // swap and the epochChanged emit happen SYNCHRONOUSLY (before awaiting
      // the IndexedDB clear) so the bridge marks the doc for rebase before the
      // step-2 frame — which arrives as a later message — is processed.
      const cleared = this.resetDocState(docId, entry, epoch);
      this.epochChanged.emit(docId);
      await cleared;
    }
  }

  onDocPayload(docId: string, payload: Uint8Array): void {
    const entry = this.docs.get(docId);
    if (!entry) return;
    const result = readMessage(entry.doc, entry.awareness, payload, ORIGIN_REMOTE);
    if (result.kind === "sync") {
      if (result.reply) {
        // Answering the server's step 1 with our tail — a write frame, so
        // viewers must stay silent (server would REJECT read-only, forcing a
        // resync loop).
        if (this.sender?.canWrite()) this.sender.sendDoc(docId, result.reply);
      } else if (entry.awaitingStep2) {
        // The applied frame is the initial step 2: sync is complete.
        entry.awaitingStep2 = false;
        entry.synced = true;
        this.flushOutbox(docId, entry);
        this.synced.emit(docId);
      }
    } else if (result.reply) {
      // Awareness query answered; relay regardless of role (reads allowed).
      this.sender?.sendDoc(docId, result.reply);
    }
  }

  onRejected(docId: string, reason: Reason, _detail: string): void {
    if (docId === "") return; // connection-scoped (e.g. RATE_LIMITED); no resync
    this.rejected.emit({ docId, reason });
    // Full-doc resync after any doc-scoped rejection (protocol spec §Doc
    // schema): drop state, resubscribe with an empty SV.
    void this.fullResync(docId);
  }

  /** Drop a doc's local state entirely and resubscribe from scratch. */
  async fullResync(docId: string): Promise<void> {
    const entry = this.docs.get(docId);
    if (!entry) return;
    await this.resetDocState(docId, entry, entry.epoch);
    // Resubscribe with the (now-empty) SV → server sends full state.
    this.sender?.subscribe(docId, entry.isMeta ? Mode.MODE_LIVE : Mode.MODE_BACKGROUND);
  }

  // --- internals -----------------------------------------------------------

  private onUpdate(
    docId: string,
    entry: DocEntry,
    update: Uint8Array,
    origin: unknown,
  ): void {
    if (origin === ORIGIN_LOAD) return; // replay of already-persisted state
    void this.persistAndMaybeCompact(docId, entry, update);
    if (origin === ORIGIN_LOCAL) {
      if (entry.synced) {
        this.sendUpdate(docId, entry, update);
      } else {
        entry.outbox.push(update); // buffer until the exchange completes
      }
    } else if (origin === ORIGIN_REMOTE && entry.synced) {
      // Steady-state remote update → the bridge writes it out to disk.
      this.remoteChange.emit({ docId, isMeta: entry.isMeta });
    }
  }

  private async persistAndMaybeCompact(
    docId: string,
    entry: DocEntry,
    update: Uint8Array,
  ): Promise<void> {
    await this.persistence.appendUpdate(docId, update);
    entry.updateCount++;
    if (entry.updateCount >= COMPACT_THRESHOLD) {
      const state = Y.encodeStateAsUpdate(entry.doc);
      await this.persistence.compactDoc(docId, state, entry.epoch);
      entry.updateCount = 0;
    }
  }

  private sendUpdate(docId: string, entry: DocEntry, update: Uint8Array): void {
    if (!this.sender?.canWrite()) return;
    void entry;
    // Wrap the raw Yjs update in a y-protocols Update frame.
    this.sender.sendDoc(docId, encodeUpdate(update));
  }

  private flushOutbox(docId: string, entry: DocEntry): void {
    if (entry.outbox.length === 0) return;
    const pending = entry.outbox;
    entry.outbox = [];
    for (const update of pending) this.sendUpdate(docId, entry, update);
  }

  /**
   * Reset a doc to an empty Y.Doc, dropping persisted + in-memory state. The
   * in-memory swap is done *synchronously* (before the first await) so a
   * step-2 frame arriving right after the SubscribeAck applies to the fresh
   * doc, never the destroyed one; the IndexedDB clear then finishes async.
   */
  private resetDocState(docId: string, entry: DocEntry, epoch: number): Promise<void> {
    entry.doc.off("update", entry.observer);
    entry.doc.destroy();
    const doc = new Y.Doc();
    entry.doc = doc;
    entry.awareness = new Awareness(doc);
    entry.epoch = epoch;
    entry.synced = false;
    entry.awaitingStep2 = true;
    entry.updateCount = 0;
    entry.outbox = [];
    entry.observer = (update, origin) => this.onUpdate(docId, entry, update, origin);
    doc.on("update", entry.observer);
    return this.persistence.dropDoc(docId);
  }

  /** Forget a doc (unsubscribe + destroy). */
  async drop(docId: string): Promise<void> {
    const entry = this.docs.get(docId);
    if (!entry) return;
    this.sender?.unsubscribe(docId);
    entry.doc.off("update", entry.observer);
    entry.doc.destroy();
    this.docs.delete(docId);
    await this.persistence.dropDoc(docId);
  }

  /** Destroy every doc (shutdown). */
  close(): void {
    for (const entry of this.docs.values()) {
      entry.doc.off("update", entry.observer);
      entry.doc.destroy();
    }
    this.docs.clear();
  }
}
